import type {SocialAccount} from '@Components/social-media/account.vo';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PostState} from '@Components/social-media/post.vo';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {WizardFlowAdditionalData} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import type {GalleryPageURI} from '@Components/design-gallery/design-gallery.types';
import {GallerySizeURI, GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import type {SocialPlatform} from '@Components/social-media/social-media.types';
import {isLocal} from '@Libraries/environment-library';

const BLOCKED_SCHEDULE_STATES = new Set<PostState>([PostState.SCHEDULED, PostState.PUBLISHING, PostState.DRAFT, PostState.ERROR, PostState.SCHEDULE_ERROR]);

export const isAccountRelatedStep = (step: SocialMediaWizardStep): boolean => {
  return step === SocialMediaWizardStep.ACCOUNT_CONNECTION || step === SocialMediaWizardStep.SELECT_ACCOUNTS || step === SocialMediaWizardStep.CONNECT_ACCOUNTS;
};

export const shouldShowScheduleDetails = (post: SocialPost | undefined, step: SocialMediaWizardStep): boolean => {
  if (!post?.state) {
    return false;
  }

  if (BLOCKED_SCHEDULE_STATES.has(post.state)) {
    return false;
  }

  return !!(post.publishOn && post.timeZoneOffset !== null && step === SocialMediaWizardStep.ADD_DETAILS);
};

interface NextButtonConfig {
  text: string;
  icon: string;
}

interface GetNextButtonConfigOpts {
  step: SocialMediaWizardStep;
  post: SocialPost | undefined;
  isSelectTemplateFlow: boolean;
}

export const getNextButtonConfig = (opts: GetNextButtonConfigOpts): NextButtonConfig => {
  const {step, post, isSelectTemplateFlow} = opts;

  if (isSelectTemplateFlow && isAccountRelatedStep(step)) {
    return {text: window.i18next.t('pmwjs_next_edit_template'), icon: 'icon-arrow-right'};
  }

  if (step === SocialMediaWizardStep.ADD_DETAILS) {
    if (shouldShowScheduleDetails(post, step)) {
      return {text: window.i18next.t('pmwjs_schedule'), icon: 'icon-calendar'};
    }

    return {text: window.i18next.t('pmwjs_publish'), icon: 'icon-publish-arrow'};
  }

  return {text: window.i18next.t('pmwjs_next'), icon: 'icon-arrow-right'};
};

export const getLoadedTextByPublishingAction = (publishingAction: SocialPostPublishingAction): string => {
  switch (publishingAction) {
    case SocialPostPublishingAction.PUBLISH:
      return window.i18next.t('pmwjs_published');
    case SocialPostPublishingAction.SCHEDULE:
      return window.i18next.t('pmwjs_scheduled');
    case SocialPostPublishingAction.SAVE:
      return window.i18next.t('pmwjs_saved');
    default:
      return window.i18next.t('pmwjs_published');
  }
};

export const getLoadingTextByPublishingAction = (publishingAction: SocialPostPublishingAction): string => {
  switch (publishingAction) {
    case SocialPostPublishingAction.PUBLISH:
      return window.i18next.t('pmwjs_publishing');
    case SocialPostPublishingAction.SCHEDULE:
      return window.i18next.t('pmwjs_scheduling');
    case SocialPostPublishingAction.SAVE:
      return window.i18next.t('pmwjs_saving');
    default:
      return window.i18next.t('pmwjs_publishing');
  }
};

export const getFirstWizardStep = (): SocialMediaWizardStep => {
  return Object.keys(SocialMediaWizardStep)[0] as SocialMediaWizardStep;
};

export const doesWizardStepNeedProgressOnBar = (step: SocialMediaWizardStep, accounts: SocialAccount[] = []): boolean => {
  switch (step) {
    case SocialMediaWizardStep.SELECT_DESIGN:
      return true;
    case SocialMediaWizardStep.CONNECT_ACCOUNTS:
      return accounts.length === 0;
    case SocialMediaWizardStep.ACCOUNT_CONNECTION:
      return false;
    case SocialMediaWizardStep.SELECT_ACCOUNTS:
      return true;
    case SocialMediaWizardStep.ADD_DETAILS:
      return true;
    case SocialMediaWizardStep.PUBLISH:
      return false;
    default:
      return false;
  }
};

const getStepForAccounts = (accounts: SocialAccount[], platformToEmphasize?: SocialPlatform): SocialMediaWizardStep => {
  if (platformToEmphasize) {
    if (doesAnyAccountHavePlatform(accounts, platformToEmphasize)) {
      return SocialMediaWizardStep.SELECT_ACCOUNTS;
    }

    return SocialMediaWizardStep.ACCOUNT_CONNECTION;
  }

  if (accounts.length > 0) {
    return SocialMediaWizardStep.SELECT_ACCOUNTS;
  }

  return SocialMediaWizardStep.CONNECT_ACCOUNTS;
};

const doesAnyAccountHavePlatform = (accounts: SocialAccount[], platform: SocialPlatform): boolean => {
  return accounts.some(function (account) {
    return account.type === platform;
  });
};

const isSpecificTemplateNotSelected = (wizardFlowData: WizardFlowAdditionalData | null): boolean => {
  return !wizardFlowData?.template;
};

export const getNextWizardStep = (
  post: SocialPost | undefined = undefined,
  accounts: SocialAccount[] = [],
  step: SocialMediaWizardStep | null = null,
  wizardFlowData: WizardFlowAdditionalData | null = null
): SocialMediaWizardStep | null => {
  if (!step && isSpecificTemplateNotSelected(wizardFlowData)) {
    if (!post) {
      return SocialMediaWizardStep.SELECT_DESIGN;
    }

    return getStepForAccounts(accounts, wizardFlowData?.emphasizedPlatform);
  }

  if (!step && wizardFlowData?.template) {
    return getStepForAccounts(accounts, wizardFlowData.emphasizedPlatform);
  }

  switch (step) {
    case SocialMediaWizardStep.SELECT_DESIGN:
      return getStepForAccounts(accounts, wizardFlowData?.emphasizedPlatform);
    case SocialMediaWizardStep.CONNECT_ACCOUNTS:
      return SocialMediaWizardStep.ACCOUNT_CONNECTION;
    case SocialMediaWizardStep.ACCOUNT_CONNECTION:
      return SocialMediaWizardStep.SELECT_ACCOUNTS;
    case SocialMediaWizardStep.SELECT_ACCOUNTS:
      if (wizardFlowData && (wizardFlowData.isGalleryFlow || wizardFlowData.template)) {
        return SocialMediaWizardStep.EDIT_TEMPLATE;
      }

      return SocialMediaWizardStep.ADD_DETAILS;

    case SocialMediaWizardStep.EDIT_TEMPLATE:
      return SocialMediaWizardStep.ADD_DETAILS;
    case SocialMediaWizardStep.ADD_DETAILS:
      return SocialMediaWizardStep.PUBLISH;
    default:
      return null;
  }
};

export const getStartingURIForGallery = (): GalleryPageURI => {
  // Templates exist on local for this category
  if (isLocal()) {
    return GalleryThemeURI.RETAIL;
  }

  return GallerySizeURI.SOCIAL_MEDIA;
};

export const isInvalidStepCombination = (prevStep: SocialMediaWizardStep | null, currentStep: SocialMediaWizardStep, post: SocialPost | null): boolean => {
  return (prevStep === SocialMediaWizardStep.SELECT_DESIGN || currentStep === SocialMediaWizardStep.SELECT_DESIGN) && post !== null;
};
